import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

// Section 4: the fit rules live here, editable by Taylor. The judge reads them on every email.
export function RulesEditor({ canEdit }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/bd/rules").then((x) => setR(x.data)).catch(() => setR(null)); }, []);
  if (!r) return <div className="text-sm text-slate-400 py-12 text-center">Loading…</div>;
  const lines = (arr) => (arr || []).join("\n");
  const setList = (k) => (e) => setR({ ...r, [k]: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) });
  const save = async () => {
    setBusy(true);
    try {
      const patch = { min_mwdc: r.min_mwdc, confidence_floor: r.confidence_floor, quiet_days: r.quiet_days, alert_email: r.alert_email, mode: r.mode, enabled: r.enabled,
        qualified: r.qualified, screened_out: r.screened_out, check_me: r.check_me, existing_client: r.existing_client };
      const x = await api.put("/bd/rules", patch); setR(x.data); toast.success("Rules saved — every new email is judged against them");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const dis = !canEdit;
  return (
    <div className="space-y-4" data-testid="bd-rules">
      <Card className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div><Label className="text-xs">Minimum size (MWdc)</Label><Input type="number" step="0.5" value={r.min_mwdc} disabled={dis} onChange={(e) => setR({ ...r, min_mwdc: e.target.value })} data-testid="bd-rule-min-mw" /></div>
        <div><Label className="text-xs">Confidence floor</Label><Input type="number" step="0.05" min="0" max="1" value={r.confidence_floor} disabled={dis} onChange={(e) => setR({ ...r, confidence_floor: e.target.value })} data-testid="bd-rule-confidence" /></div>
        <div><Label className="text-xs">“Went quiet” after (days)</Label><Input type="number" value={r.quiet_days} disabled={dis} onChange={(e) => setR({ ...r, quiet_days: Number(e.target.value) })} data-testid="bd-rule-quiet" /></div>
        <div><Label className="text-xs">Alerts go to</Label><Input value={r.alert_email || ""} disabled={dis} onChange={(e) => setR({ ...r, alert_email: e.target.value })} data-testid="bd-rule-alert-email" /></div>
        <div>
          <Label className="text-xs">Mode</Label>
          <select value={r.mode} disabled={dis} onChange={(e) => setR({ ...r, mode: e.target.value })} className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="bd-rule-mode">
            <option value="live">Live — tray, notes, suggestions</option><option value="shadow">Shadow — tray and notes only</option><option value="off">Off — judge and record only</option>
          </select>
          <div className="flex items-center gap-2 mt-2"><Switch checked={!!r.enabled} disabled={dis} onCheckedChange={(v) => setR({ ...r, enabled: v })} data-testid="bd-rule-enabled" /><span className="text-xs text-slate-600">Agent on (kill switch)</span></div>
        </div>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[["qualified", "Qualified when all of"], ["screened_out", "Screened out"], ["check_me", "Check me"]].map(([k, title]) => (
          <Card key={k} className="p-4">
            <Label className="text-xs">{title}</Label>
            <Textarea rows={7} value={lines(r[k])} disabled={dis} onChange={setList(k)} className="text-sm mt-1" data-testid={`bd-rule-${k}`} />
            <div className="text-[11px] text-slate-400 mt-1">One rule per line. {"{min_mwdc}"} and {"{confidence_floor}"} fill in from above.</div>
          </Card>
        ))}
      </div>
      <Card className="p-4">
        <Label className="text-xs">From a client we already have</Label>
        <Input value={r.existing_client || ""} disabled={dis} onChange={(e) => setR({ ...r, existing_client: e.target.value })} className="mt-1" data-testid="bd-rule-existing" />
      </Card>
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-400">{r.updated_at ? `Last changed ${new Date(r.updated_at).toLocaleString()} by ${r.updated_by}` : "Defaults (approved by Taylor 2026-10-23)"}</div>
        {canEdit ? <Button onClick={save} disabled={busy} className="bg-[#003E87] hover:bg-[#002a5c] text-white" data-testid="bd-rules-save">Save rules</Button> : <span className="text-xs text-slate-400">Only Taylor can change the rules.</span>}
      </div>
    </div>
  );
}
